/**
 * Analytics module for ClubConnect tracking in the web application.
 * 
 * This module provides utilities for tracking ClubConnect-specific events
 * using Google Tag Manager (GTM). All events are sent to GTM's dataLayer
 * for processing and forwarding to configured analytics services.
 * 
 * Any errors from this module should be caught and logged to prevent
 * blocking the user experience.
 */

import TagManager from 'react-gtm-module';

export const EVENT_NAMES = {
  // ClubConnect Events
  CLUB_CONNECT_PROFILE_SWITCH: 'club_connect_profile_switch',
  CLUB_CONNECT_WORKOUT_ASSIGNED: 'club_connect_workout_assigned',
  CLUB_CONNECT_CLIENT_GROUP_CREATED: 'club_connect_client_group_created',
  CLUB_CONNECT_CLIENT_GROUP_UPDATED: 'club_connect_client_group_updated',
  CLUB_CONNECT_CHAT_INITIATED: 'club_connect_chat_initiated',
  CLUB_CONNECT_CHAT_MESSAGE_SENT: 'club_connect_chat_message_sent',
};

/**
 * Universal method to track events via GTM dataLayer
 * @param {string} event - Event name
 * @param {object} eventData - Event parameters
 */
export const track = (event, eventData = {}) => {
  try {
    TagManager.dataLayer({
      dataLayer: {
        event,
        ...eventData,
      },
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

/**
 * Tracks profile switching between personal and club profiles
 * @param {string} userId - User's database ID
 * @param {string} fromProfileType - 'personal' | 'club'
 * @param {string} toProfileType - 'personal' | 'club'
 * @param {object} clubData - Club information (if switching to/from club)
 */
export const logClubConnectProfileSwitch = (
  userId,
  fromProfileType,
  toProfileType,
  clubData = null,
) => {
  const eventData = {
    user_id: userId,
    from_profile_type: fromProfileType,
    to_profile_type: toProfileType,
    timestamp: new Date().toISOString(),
  };

  if (clubData) {
    eventData.club_id = clubData.ClubId || null;
    eventData.club_name = clubData.ClubName || null;
    eventData.location_id = clubData.Locations?.Id || null;
    eventData.location_name = clubData.Locations?.Name || null;
  }

  track(EVENT_NAMES.CLUB_CONNECT_PROFILE_SWITCH, eventData);
};

/**
 * Tracks workout assignments from club profile
 * @param {string} userId - Trainer's user ID
 * @param {object} clubData - Active club profile data
 * @param {string} workoutType - 'individual' | 'program' | 'custom'
 * @param {number} clientCount - Number of clients assigned
 * @param {string} assignmentMethod - 'individual' | 'group'
 * @param {string} clientGroupId - Group ID if assigned to group
 */
export const logClubConnectWorkoutAssigned = (
  userId,
  clubData,
  workoutType,
  clientCount,
  assignmentMethod,
  clientGroupId = null,
) => {
  const eventData = {
    user_id: userId,
    club_id: clubData?.ClubId || null,
    club_name: clubData?.ClubName || null,
    location_id: clubData?.Locations?.Id || null,
    workout_type: workoutType,
    client_count: clientCount,
    assignment_method: assignmentMethod,
    client_group_id: clientGroupId,
    timestamp: new Date().toISOString(),
  };

  track(EVENT_NAMES.CLUB_CONNECT_WORKOUT_ASSIGNED, eventData);
};

/**
 * Tracks client group creation from club profile
 * @param {string} userId - Trainer's user ID
 * @param {object} clubData - Active club profile data
 * @param {string} groupId - Created group ID
 * @param {string} groupName - Group name
 * @param {number} clientCount - Number of clients in group
 */
export const logClubConnectClientGroupCreated = (
  userId,
  clubData,
  groupId,
  groupName,
  clientCount,
) => {
  const eventData = {
    user_id: userId,
    club_id: clubData?.ClubId || null,
    club_name: clubData?.ClubName || null,
    location_id: clubData?.Locations?.Id || null,
    group_id: groupId,
    group_name: groupName,
    client_count: clientCount,
    timestamp: new Date().toISOString(),
  };

  track(EVENT_NAMES.CLUB_CONNECT_CLIENT_GROUP_CREATED, eventData);
};

/**
 * Tracks client group updates from club profile
 * @param {string} userId - Trainer's user ID
 * @param {object} clubData - Active club profile data
 * @param {string} groupId - Updated group ID
 * @param {string} groupName - Group name
 * @param {number} clientCount - Number of clients in group
 * @param {string} updateType - 'name_change' | 'members_added' | 'members_removed'
 */
export const logClubConnectClientGroupUpdated = (
  userId,
  clubData,
  groupId,
  groupName,
  clientCount,
  updateType,
) => {
  const eventData = {
    user_id: userId,
    club_id: clubData?.ClubId || null,
    club_name: clubData?.ClubName || null,
    location_id: clubData?.Locations?.Id || null,
    group_id: groupId,
    group_name: groupName,
    client_count: clientCount,
    update_type: updateType,
    timestamp: new Date().toISOString(),
  };

  track(EVENT_NAMES.CLUB_CONNECT_CLIENT_GROUP_UPDATED, eventData);
};

/**
 * Tracks chat initiation from club profile
 * @param {string} userId - User's ID
 * @param {object} clubData - Active club profile data
 * @param {string} chatType - 'individual' | 'group'
 * @param {number} participantCount - Number of participants
 * @param {string} clientGroupId - Group ID if group chat
 */
export const logClubConnectChatInitiated = (
  userId,
  clubData,
  chatType,
  participantCount,
  clientGroupId = null,
) => {
  const eventData = {
    user_id: userId,
    club_id: clubData?.ClubId || null,
    club_name: clubData?.ClubName || null,
    location_id: clubData?.Locations?.Id || null,
    chat_type: chatType,
    participant_count: participantCount,
    client_group_id: clientGroupId,
    timestamp: new Date().toISOString(),
  };

  track(EVENT_NAMES.CLUB_CONNECT_CHAT_INITIATED, eventData);
};

/**
 * Tracks chat message sending from club profile
 * @param {string} userId - User's ID
 * @param {object} clubData - Active club profile data
 * @param {string} chatType - 'individual' | 'group'
 * @param {string} messageType - 'text' | 'image' | 'file'
 */
export const logClubConnectChatMessageSent = (
  userId,
  clubData,
  chatType,
  messageType = 'text',
) => {
  const eventData = {
    user_id: userId,
    club_id: clubData?.ClubId || null,
    club_name: clubData?.ClubName || null,
    location_id: clubData?.Locations?.Id || null,
    chat_type: chatType,
    message_type: messageType,
    timestamp: new Date().toISOString(),
  };

  track(EVENT_NAMES.CLUB_CONNECT_CHAT_MESSAGE_SENT, eventData);
};
